import { useMemo, useState } from "react";
import React from "react";
import { Box } from "../../../components/Box";
import { Flex } from "../../../components/Flex";
import { ReviewStatusPill } from "../../../components/ReviewStatusPill";
import { SubjectTypeIcon } from "../../../components/SubjectTypeIcon";
import { Heading, CardLine } from "../../IndividualView";
import { useProfileStatusList } from "../../../data/useProfileStatusList";
import {
  ProfileStatusBgColours,
  ProfileStatusColours,
} from "../../../components/layoutConfig";
import { useUpdateProfileStatus } from "../../../data/useUpdateProfileStatus";
import { ProfileStatus, UserRef } from "../../../types/Profile";
import { Modal } from "../../../components/Modal";
import { ProfileStatusSlug } from "../../../context/ProfileStatusContext";
import { showError } from "../../../utils/error-handling";
import { notify } from "../../../utils/notify";
import { Paragraph } from "../../../components/Paragraph";
import { useUsers } from "../../../data/useUsers";
import { IconButton } from "../../../components/IconButton";
import { GrTrash } from "react-icons/gr";
import Select from "../../../components/Select";
import { H2 } from "../../../components/Heading";
import { Button } from "../../../components/Button";
import { useAuth } from "../../../context/AuthContext";
import {
  PatchProfileParams,
  usePatchProfile,
} from "../../../data/usePatchProfile";
import { MdPerson } from "react-icons/md";
import theme from "../../../theme";
import { formatDate } from "../../../utils/formatters";
import { DatePicker } from "../../../components/DatePicker";
import { DateTime } from "luxon";

type StatusOption = {
  id: number;
  label: string;
  status?: string;
};

type UserOption = {
  value: number;
  label: string;
  firstName: string;
  lastName: string;
  colour?: string;
};

type AssignRole = "owner" | "reviewer";

type PendingAssignment = {
  role: AssignRole;
  user: UserOption | null;
};

const REVIEW_OPTIONS = [
  { label: "Every 6 months", value: "6_month" },
  { label: "Every year", value: "1_year" },
  { label: "Every 3 years", value: "3_year" },
  { label: "Every 5 years", value: "5_year" },
];

/**
 * Mirrors add_periodic_review_date in the backend profile serializer, so the
 * date shown here is the one the API would work out for itself if we sent none.
 */
const REVIEW_INTERVAL_DAYS: Record<string, number> = {
  "6_month": 180,
  "1_year": 365,
  "3_year": 1095,
  "5_year": 1825,
};

const suggestReviewDate = (frequency?: string | null) => {
  const days = frequency ? REVIEW_INTERVAL_DAYS[frequency] : undefined;
  return days ? DateTime.now().plus({ days }).startOf("day").toJSDate() : null;
};

const toYMD = (d?: Date | null) =>
  d instanceof Date && !isNaN(d.getTime())
    ? DateTime.fromJSDate(d).toISODate()
    : null;

const InitialCircle = ({
  firstName,
  title,
  bg = "#E5E7EB",
}: {
  firstName?: string | null;
  title?: string;
  bg?: string;
}) => {
  const initial = firstName?.trim()?.charAt(0)?.toUpperCase() ?? "?";
  return (
    <Box
      width={28}
      height={28}
      borderRadius="50%"
      background={bg}
      display="flex"
      alignItems="center"
      justifyContent="center"
      fontSize={12}
      fontWeight={700}
      color="#374151"
      title={title ?? firstName ?? ""}
    >
      <Paragraph> {initial}</Paragraph>
    </Box>
  );
};

const EmptyCircle = ({
  title,
  onClick,
}: {
  title: string;
  onClick: () => void;
}) => {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e: any) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      title={title}
      style={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        border: "2px dashed #D1D5DB",
        background: "#F9FAFB",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      <MdPerson size={16} color={theme.colors.gray[50]} />
    </Box>
  );
};

export const ProfileHeader = ({
  isEntity,
  displayName,
  leftMeta,
  entityTypeName,
  registrationNumber,
  reviewStatus,
  profileId,
  profileDisplayName,
  owner,
  reviewer,
  profileReference,
  profileExternalReference,
  periodicReview,
  periodicReviewDate,
}: {
  isEntity: boolean;
  displayName: string;
  leftMeta: string;
  entityTypeName?: string;
  registrationNumber?: string;
  reviewStatus: ProfileStatus | null;
  profileId: number;
  profileDisplayName: string;
  owner?: UserRef | null;
  reviewer?: UserRef | null;
  profileReference?: string | null;
  profileExternalReference?: string | null;
  periodicReview?: string;
  periodicReviewDate?: string;
}) => {

  const { hasPermission } = useAuth();
  const { data: profileStatusList } = useProfileStatusList();
  const { mutate: updateStatus, isPending: isUpdating } =
    useUpdateProfileStatus();
  const { mutate: patchProfile, isPending: isPatchingProfile } =
    usePatchProfile();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<StatusOption | null>(null);
  const [reviewFrequency, setReviewFrequency] = useState<string | null>(null);
  const [isPeriodicReviewModalOpen, setIsPeriodicReviewModalOpen] =
    useState(false);

  const statuses: StatusOption[] = useMemo(() => {
    return Array.isArray(profileStatusList?.data) ? profileStatusList.data : [];
  }, [profileStatusList]);

  const currentStatusLabel = reviewStatus?.label ?? "-";
  const currentStatusSlug = (reviewStatus?.status ??
    "") as keyof typeof ProfileStatusColours;

  const currentColor =
    (currentStatusSlug && ProfileStatusColours[currentStatusSlug]) || "gray.60";

  const { data: users } = useUsers();

  const userOptions: UserOption[] =
    users?.data?.results?.map(({ first_name, last_name, id, colour }) => ({
      value: id,
      label: `${first_name} ${last_name}`,
      firstName: first_name,
      lastName: last_name,
      colour,
    })) ?? [];

  const selectedOwnerOption =
    owner?.id != null ? userOptions.find((u) => u.value === owner.id) : null;

  const selectedReviewerOption =
    reviewer?.id != null
      ? userOptions.find((u) => u.value === reviewer.id)
      : null;

  const [isOwnerReviewerModalOpen, setIsOwnerReviewerModalOpen] =
    useState(false);

  const [pendingAssignment, setPendingAssignment] =
    useState<PendingAssignment | null>(null);

  const [isAssignConfirmOpen, setIsAssignConfirmOpen] = useState(false);

  const [shouldRestoreOwnerReviewerModal, setShouldRestoreOwnerReviewerModal] =
    useState(false);

  const [reviewDate, setReviewDate] = useState<Date | null>(null);
  const [isReviewDateEdited, setIsReviewDateEdited] = useState(false);

  const openOwnerReviewerModal = () => setIsOwnerReviewerModalOpen(true);
  const closeOwnerReviewerModal = () => setIsOwnerReviewerModalOpen(false);

  // Seed from the saved profile every time it opens, so an abandoned edit is
  // never carried back into the next one.
  const openPeriodicReviewModal = () => {
    setReviewFrequency(periodicReview ?? null);
    setReviewDate(
      periodicReviewDate
        ? DateTime.fromISO(periodicReviewDate).toJSDate()
        : null,
    );
    setIsReviewDateEdited(false);
    setIsPeriodicReviewModalOpen(true);
  };

  const closePeriodicReviewModal = () => setIsPeriodicReviewModalOpen(false);

  const openAssignConfirm = (next: PendingAssignment) => {
    if (isOwnerReviewerModalOpen) {
      setShouldRestoreOwnerReviewerModal(true);
      setIsOwnerReviewerModalOpen(false);
    }

    setPendingAssignment(next);
    setIsAssignConfirmOpen(true);
  };

  const closeAssignConfirm = () => {
    setIsAssignConfirmOpen(false);
    setPendingAssignment(null);

    if (shouldRestoreOwnerReviewerModal) {
      setIsOwnerReviewerModalOpen(true);
      setShouldRestoreOwnerReviewerModal(false);
    }
  };

  const applyAssignment = (e?: any) => {
    e?.preventDefault?.();

    if (!pendingAssignment) return;

    const payload =
      pendingAssignment.role === "owner"
        ? { profileId, owner: pendingAssignment.user?.value ?? null }
        : { profileId, reviewer: pendingAssignment.user?.value ?? null };

    patchProfile(payload, {
      onSuccess: () => {
        notify(
          pendingAssignment.user
            ? `${pendingAssignment.role} updated to ${pendingAssignment.user.label}`
            : `${pendingAssignment.role} cleared`,
          { position: "bottom-left", type: "success" },
        );

        setTimeout(() => {
          closeAssignConfirm();
        }, 0);
      },

      onError: (err) =>
        showError(
          err,
          `Error occurred while updating ${pendingAssignment.role}`,
        ),
    });
  };

  const closeDropdown = () => setIsDropdownOpen(false);

  const openStatusModal = (s: StatusOption) => {
    closeDropdown();
    if (s.label === currentStatusLabel) return;

    setPendingStatus(s);
    setIsStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    setIsStatusModalOpen(false);
    setPendingStatus(null);
  };

  const handleStatusConfirm = (commentFromModal: string) => {
    if (!pendingStatus) return;

    updateStatus(
      {
        profileId,
        newStatusId: pendingStatus.id,
        comment: commentFromModal,
      },
      {
        onSuccess: () => {
          closeStatusModal();
          notify("Profile status updated successfully!", {
            position: "bottom-left",
            type: "success",
          });
        },
        onError: (err) =>
          showError(err, "Error occurred while updating profile status"),
      },
    );
  };
  const selectedPeriodicReviewOption = React.useMemo(
    () =>
      reviewFrequency
        ? REVIEW_OPTIONS.find((opt) => opt.value === reviewFrequency) ?? null
        : null,
    [reviewFrequency],
  );

  // "never" is a legacy value: it was a selectable frequency before removing a
  // schedule became its own action, and it carries no date.
  const periodicReviewSummary = !periodicReview
    ? "Not scheduled yet."
    : periodicReview === "never"
    ? "Not required"
    : `Scheduled for ${formatDate(periodicReviewDate)}`;

  // Changing the frequency re-suggests the due date, unless the reviewer has
  // already picked one of their own.
  const handleFrequencyChange = (option: any) => {
    const nextFrequency = option?.value ?? null;
    setReviewFrequency(nextFrequency);

    if (!isReviewDateEdited) {
      setReviewDate(suggestReviewDate(nextFrequency));
    }
  };

  const handleReviewDateChange = (date: any) => {
    setIsReviewDateEdited(true);
    setReviewDate(date ?? null);
  };

  const submitPeriodicReview = (
    payload: Pick<
      PatchProfileParams,
      "periodic_review" | "periodic_review_date"
    >,
    successMessage: string,
  ) => {
    patchProfile(
      { profileId, ...payload },
      {
        onSuccess: () => {
          notify(successMessage, {
            position: "bottom-left",
            type: "success",
          });

          setTimeout(() => {
            setIsPeriodicReviewModalOpen(false);
          }, 0);
        },

        onError: (err) =>
          showError(err, "Error occurred while updating periodic review"),
      },
    );
  };

  const savePeriodicReview = () =>
    submitPeriodicReview(
      {
        periodic_review: reviewFrequency,
        periodic_review_date: toYMD(reviewDate),
      },
      "Periodic review scheduled",
    );

  const removePeriodicReview = () =>
    submitPeriodicReview(
      { periodic_review: null, periodic_review_date: null },
      "Periodic review removed",
    );
  return (
    <Box
      background={theme.colors.white}
      border={`1px solid ${theme.colors.divider}`}
      borderRadius={12}
      p={16}
      mb={14}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Flex gap="12" alignItems="center">
          <SubjectTypeIcon isEntity={isEntity} size={44} />

          <Box>
            <Heading fontSize={18} fontWeight={700}>
              {displayName}
            </Heading>
            {isEntity && (
              <CardLine color={theme.colors.muted}>
                Entity Type: {entityTypeName || "-"}
              </CardLine>
            )}
            <CardLine color={theme.colors.muted}>
              Our reference: {profileReference || "-"}
            </CardLine>
            <CardLine color={theme.colors.muted}>
              Your Reference: {profileExternalReference || "-"}
            </CardLine>
            {isEntity ? (
              <CardLine color={theme.colors.muted}>
                Registration number: {registrationNumber || "-"}
              </CardLine>
            ) : (
              <CardLine color={theme.colors.muted}>{leftMeta}</CardLine>
            )}
          </Box>
        </Flex>
        <Flex alignItems="end" gap="12">
          <Box borderRight={`1px solid ${theme.colors.muted}`}>
            <CardLine color="black" mb={6}>
              Periodic Review
            </CardLine>
            <CardLine justifyContent="flex-start" mr="10px">
              {periodicReviewSummary}
            </CardLine>
            <Button
              variant="blueText"
              display="inline-flex"
              px={0}
              height="auto"
              fontSize="1"
              fontWeight={600}
              onClick={openPeriodicReviewModal}
              isDisabled={!hasPermission("profile.periodic_review")}
              title={
                hasPermission("profile.periodic_review")
                  ? undefined
                  : "You don't have permission to edit the periodic review"
              }
            >
              {periodicReview ? "Edit" : "Add"}
            </Button>
          </Box>

          <Box>
            <CardLine color="black" mb={6}>
              Review Status
            </CardLine>

            <Flex justifyContent="flex-end" alignItems="center" gap="8">
              {/* Owner */}
              <Box
                style={{
                  cursor: hasPermission("profile.owner")
                    ? "pointer"
                    : "not-allowed",
                  opacity: hasPermission("profile.owner") ? 1 : 0.4,
                }}
                onClick={
                  hasPermission("profile.owner")
                    ? openOwnerReviewerModal
                    : undefined
                }
              >
                {owner?.first_name ? (
                  <InitialCircle
                    firstName={owner.first_name}
                    title={
                      hasPermission("profile.owner")
                        ? `Owner: ${[owner.first_name, owner.last_name]
                            .filter(Boolean)
                            .join(" ")}`
                        : "You don't have permission to assign a profile owner"
                    }
                  />
                ) : (
                  <EmptyCircle
                    title={
                      hasPermission("profile.owner")
                        ? "Assign an Owner"
                        : "You don't have permission to assign a profile owner"
                    }
                    onClick={
                      hasPermission("profile.owner")
                        ? openOwnerReviewerModal
                        : () => {}
                    }
                  />
                )}
              </Box>

              {/* Reviewer */}
              <Box
                style={{
                  cursor: hasPermission("profile.reviewer")
                    ? "pointer"
                    : "not-allowed",
                  opacity: hasPermission("profile.reviewer") ? 1 : 0.4,
                }}
                onClick={
                  hasPermission("profile.reviewer")
                    ? openOwnerReviewerModal
                    : undefined
                }
              >
                {reviewer?.first_name ? (
                  <InitialCircle
                    firstName={reviewer.first_name}
                    title={
                      hasPermission("profile.reviewer")
                        ? `Reviewer: ${[
                            reviewer.first_name,
                            reviewer.last_name,
                          ]
                            .filter(Boolean)
                            .join(" ")}`
                        : "You don't have permission to assign a reviewer"
                    }
                  />
                ) : (
                  <EmptyCircle
                    title={
                      hasPermission("profile.reviewer")
                        ? "Assign a Reviewer"
                        : "You don't have permission to assign a reviewer"
                    }
                    onClick={
                      hasPermission("profile.reviewer")
                        ? openOwnerReviewerModal
                        : () => {}
                    }
                  />
                )}
              </Box>

              {/* Status */}
              <Box position="relative" display="inline-block">
                <Box
                  style={{
                    display: "inline-block",
                    cursor: hasPermission("profile.status")
                      ? "pointer"
                      : "not-allowed",
                    opacity: hasPermission("profile.status") ? 1 : 0.4,
                  }}
                  onClick={(e) => {
                    if (!hasPermission("profile.status")) return;
                    e.stopPropagation();
                    setIsDropdownOpen((v) => !v);
                  }}
                  title={
                    hasPermission("profile.status")
                      ? undefined
                      : "You don't have permission to change the review status"
                  }
                >
                  <ReviewStatusPill
                    label={currentStatusLabel}
                    color={currentColor}
                  />
                </Box>

                {isDropdownOpen && (
                  <>
                    <Box
                      style={{ position: "fixed", inset: 0, zIndex: 40 }}
                      onClick={closeDropdown}
                    />

                    <Box
                      style={{
                        position: "absolute",
                        top: "110%",
                        right: 0,
                        background: "white",
                        boxShadow: "0px 4px 10px rgba(0,0,0,0.12)",
                        borderRadius: "12px",
                        zIndex: 50,
                        width: 220,
                        overflow: "hidden",
                        padding: 8,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {statuses.map((s) => {
                        const slug = (s.status ?? "") as ProfileStatusSlug;
                        const color = ProfileStatusColours[slug] ?? "gray.60";
                        const bgColor =
                          ProfileStatusBgColours[slug] ?? "gray.10";

                        return (
                          <Box
                            key={s.id}
                            textAlign="left"
                            style={{ padding: 6, cursor: "pointer" }}
                            onClick={() => openStatusModal(s)}
                          >
                            <ReviewStatusPill
                              label={s.label}
                              color={color}
                              bgColor={bgColor}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}
              </Box>
            </Flex>

            {isDropdownOpen && (
              <>
                <Box
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 40,
                  }}
                  onClick={closeDropdown}
                />

                <Box
                  style={{
                    position: "absolute",
                    top: "110%",
                    right: 0,
                    background: "white",
                    boxShadow: "0px 4px 10px rgba(0,0,0,0.12)",
                    borderRadius: "12px",
                    zIndex: 50,
                    width: 220,
                    overflow: "hidden",
                    padding: 8,
                  }}
                >
                  {statuses.map((s) => {
                    const slug = (s.status ?? "") as ProfileStatusSlug;

                    const color = ProfileStatusColours[slug] ?? "gray.60";
                    const bgColor = ProfileStatusBgColours[slug] ?? "gray.10";

                    return (
                      <Box
                        key={s.id}
                        textAlign="left"
                        style={{ padding: 6, cursor: "pointer" }}
                        onClick={() => openStatusModal(s)}
                      >
                        <ReviewStatusPill
                          label={s.label}
                          color={color}
                          bgColor={bgColor}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
        </Flex>
      </Flex>

      <Modal
        isClosingDisabled={isUpdating}
        isOpen={isStatusModalOpen}
        onClose={closeStatusModal}
        onModalConfirmClick={handleStatusConfirm}
        title={`Are you sure you want to update profile status from ${
          reviewStatus?.label ?? "-"
        } to ${pendingStatus?.label ?? "-"}`}
        content={`${profileDisplayName}`}
        hasSecondaryButton
        secondaryButtonText="Cancel"
        width="420px"
        primaryButtonText="Submit"
        primaryButtonVariant="primary"
        hasCommentBox
        isLoadingAction={isUpdating}
        loadingActionText="Submitting..."
      />

      <Modal
        isOpen={isOwnerReviewerModalOpen && !isAssignConfirmOpen}
        onClose={closeOwnerReviewerModal}
        hasCloseIcon
        width="420px"
      >
        <H2 mr={3}>Profile Owner and Reviewer</H2>

        <Paragraph color="gray.50" fontSize="1" mb={3}>
          This is an irreversible action, if you delete it you will not be able
          to undo the process.
        </Paragraph>

        {/* Owner */}
        <Box mb={3}>
          <Paragraph fontSize="1" color="gray.60" mb={1}>
            Profile Owner
          </Paragraph>

          <Flex alignItems="center" gap="8">
            <Box flex={1}>
              <Select
                value={selectedOwnerOption}
                options={userOptions}
                onChange={(option: UserOption) => {
                  if (option?.value === selectedOwnerOption?.value) return;
                  openAssignConfirm({ role: "owner", user: option });
                }}
                placeholder="Select owner"
                isDisabled={!hasPermission("profile.owner")}
                title={
                  hasPermission("profile.owner")
                    ? undefined
                    : "You don't have permission to assign a profile owner"
                }
              />
            </Box>

            <IconButton
              Type={GrTrash}
              color="gray.50"
              isDisabled={!hasPermission("profile.owner")}
              title={
                hasPermission("profile.owner")
                  ? undefined
                  : "You don't have permission to assign a profile owner"
              }
              onClick={() => {
                if (!hasPermission("profile.owner")) return;
                if (!selectedOwnerOption) return;
                openAssignConfirm({ role: "owner", user: null });
              }}
            />
          </Flex>
        </Box>

        {/* Reviewer */}
        <Box mb={2}>
          <Paragraph fontSize="1" color="gray.60" mb={1}>
            Reviewer
          </Paragraph>

          <Flex alignItems="center" gap="8">
            <Box flex={1}>
              <Select
                value={selectedReviewerOption}
                options={userOptions}
                onChange={(option: UserOption) => {
                  if (option?.value === selectedReviewerOption?.value) return;
                  openAssignConfirm({ role: "reviewer", user: option });
                }}
                placeholder="Select reviewer"
                isDisabled={!hasPermission("profile.reviewer")}
                title={
                  hasPermission("profile.reviewer")
                    ? undefined
                    : "You don't have permission to assign a reviewer"
                }
              />
            </Box>

            <IconButton
              Type={GrTrash}
              color="gray.50"
              isDisabled={!hasPermission("profile.reviewer")}
              title={
                hasPermission("profile.reviewer")
                  ? undefined
                  : "You don't have permission to assign a reviewer"
              }
              onClick={() => {
                if (!hasPermission("profile.reviewer")) return;
                if (!selectedReviewerOption) return;
                openAssignConfirm({ role: "reviewer", user: null });
              }}
            />
          </Flex>
        </Box>

        <Flex justifyContent="end" mt={3}>
          <Button variant="text" mr={2} onClick={closeOwnerReviewerModal}>
            Cancel
          </Button>
          <Button onClick={closeOwnerReviewerModal}>Save</Button>
        </Flex>
      </Modal>

      <Modal
        isOpen={isAssignConfirmOpen}
        onClose={closeAssignConfirm}
        isClosingDisabled={isPatchingProfile}
        title={
          pendingAssignment?.user
            ? `Are you sure you want to assign ${profileDisplayName} to ${pendingAssignment.user.label} to be it's ${pendingAssignment.role}?`
            : `Are you sure you want to remove ${pendingAssignment?.role} from ${profileDisplayName}?`
        }
        content=""
        hasSecondaryButton
        secondaryButtonText="Cancel"
        primaryButtonText={pendingAssignment?.user ? "Assign" : "Remove"}
        primaryButtonVariant="primary"
        onModalConfirmClick={applyAssignment}
        isLoadingAction={isPatchingProfile}
        loadingActionText={
          pendingAssignment?.user ? "Assigning..." : "Removing..."
        }
        width="420px"
      />

      <Modal
        isOpen={isPeriodicReviewModalOpen}
        onClose={closePeriodicReviewModal}
        isClosingDisabled={isPatchingProfile}
        hasCloseIcon
        width="520px"
      >
        <H2 mr={3}>Schedule periodic review</H2>

        <Paragraph color="gray.50" fontSize="1" mb={3}>
          Verify moves the profile to Periodic Review on the due date, then
          schedules the next one.
        </Paragraph>

        <Box mb={3}>
          <Select
            label="Review frequency"
            value={selectedPeriodicReviewOption}
            options={REVIEW_OPTIONS}
            onChange={handleFrequencyChange}
            placeholder="Select a frequency"
            isInModal
            isRequired
          />
        </Box>

        <Box mb={3}>
          <DatePicker
            width="100%"
            value={reviewDate}
            onChange={handleReviewDateChange}
            minDate={DateTime.now().toJSDate()}
            label="Next review due"
            name="periodic_review_date"
            openCalendarOnFocus={false}
          />
          <Paragraph color="gray.50" fontSize="1" mt={1}>
            Set from the frequency. Type a date or use the calendar if the first
            review is due on a different day.
          </Paragraph>
        </Box>

        <Flex justifyContent="space-between" alignItems="center" mt={4}>
          <Box>
            {periodicReview && (
              <Button
                variant="danger-text"
                px={0}
                isDisabled={
                  isPatchingProfile || !hasPermission("profile.periodic_review")
                }
                title={
                  hasPermission("profile.periodic_review")
                    ? undefined
                    : "You don't have permission to edit the periodic review"
                }
                onClick={removePeriodicReview}
              >
                Remove schedule
              </Button>
            )}
          </Box>

          <Flex>
            <Button
              variant="text"
              mr={2}
              isDisabled={isPatchingProfile}
              onClick={closePeriodicReviewModal}
            >
              Cancel
            </Button>
            <Button
              onClick={savePeriodicReview}
              isDisabled={
                !reviewFrequency ||
                isPatchingProfile ||
                !hasPermission("profile.periodic_review")
              }
              title={
                hasPermission("profile.periodic_review")
                  ? undefined
                  : "You don't have permission to edit the periodic review"
              }
            >
              {isPatchingProfile ? "Saving..." : "Save"}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </Box>
  );
};
